import React, { useMemo } from 'react';
import { HtmlHeadInclude } from '@msdyn365-commerce/core';
import { VariantDetails, VariantsByKey } from '../../../integrations/relewise/types/mapped';
import { RELEWISE_MINIMUM_STOCK_COUNT } from '../../../integrations/relewise/const';

const formatSchemaText = (value?: string): string | undefined => {
    if (!value) {
        return undefined;
    }

    const formatted = value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => line.replace(/^\-\s*/, ''))
        .join('. ')
        .replace(/\s+/g, ' ')
        .replace(/\.\s*\./g, '.')
        .trim();

    return formatted || undefined;
};

const hasStock = (variant: VariantDetails): boolean => {
    return variant.fallbackStockCount !== undefined && variant.fallbackStockCount > RELEWISE_MINIMUM_STOCK_COUNT;
};

interface MergedColorVariant {
    details: VariantDetails;
    isInStock: boolean;
}
const mergeVariantsByColor = (variantsByKey?: VariantsByKey): MergedColorVariant[] => {
    if (!variantsByKey) {
        return [];
    }

    const variantsByColor = new Map<string, MergedColorVariant>();

    Object.values(variantsByKey).forEach(variant => {
        const colorName = variant.color.name;
        const existing = variantsByColor.get(colorName);
        const isVariantInStock = hasStock(variant);

        if (!existing) {
            variantsByColor.set(colorName, {
                details: variant,
                isInStock: isVariantInStock
            });
            return;
        }

        existing.isInStock = existing.isInStock || isVariantInStock;

        if (variant.variantRecordId === variant.primarySizeVariant?.variantRecordId) {
            existing.details = variant;
        }
    });

    return Array.from(variantsByColor.values());
};

const getProductSchema = ({
    title,
    description,
    canonicalUrl,
    brand,
    productGroupId,
    material,
    currencyCode,
    variantsByKey,
    allSizes
}: Omit<Props, 'imageUrl'>) => {
    if (!title || !canonicalUrl || !productGroupId) {
        return undefined;
    }

    const colorVariants = mergeVariantsByColor(variantsByKey);

    return {
        '@context': 'https://schema.org',
        '@type': 'ProductGroup',
        '@id': `${canonicalUrl}#productgroup`,
        url: canonicalUrl,
        name: title,
        ...(description && { description: formatSchemaText(description) }),
        productGroupID: productGroupId,

        ...(brand && {
            brand: {
                '@type': 'Brand',
                name: brand
            }
        }),

        ...(material && { material: formatSchemaText(material) }),

        size: allSizes,

        variesBy: ['https://schema.org/color'],

        hasVariant: colorVariants.map(variant => {
            const colorName = variant.details.color.name;
            const variantUrl = variant.details.variantFullUrl;

            return {
                '@type': 'Product',
                '@id': `${variantUrl}#product`,
                name: `${title} - ${colorName}`,
                color: colorName,
                sku: variant.details.concatenatedId,
                productID: variant.details.variantRecordId.toString(),
                ...(variant.details.imgUrl && { image: [variant.details.imgUrl] }),
                offers: {
                    '@type': 'Offer',
                    url: variantUrl,
                    ...(currencyCode && { priceCurrency: currencyCode }),
                    ...(variant.details.finalPrice !== undefined && { price: variant.details.finalPrice }),
                    availability: variant.isInStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                    itemCondition: 'https://schema.org/NewCondition'
                }
            };
        }),

        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl
        }
    };
};

interface Props {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    imageUrl?: string;
    brand?: string;
    productGroupId?: string;
    material?: string;
    currencyCode?: string;
    variantsByKey?: VariantsByKey;
    allSizes?: string[];
}

export const BuyboxHeadMetadata = (props: Props) => {
    const { title, description, canonicalUrl, imageUrl } = props;

    const productSchema = useMemo(() => getProductSchema(props), [
        props.title,
        props.description,
        props.canonicalUrl,
        props.brand,
        props.productGroupId,
        props.material,
        props.currencyCode,
        props.variantsByKey,
        props.allSizes
    ]);

    const formattedDescription = formatSchemaText(description);

    return (
        <HtmlHeadInclude>
            {title && <title>{title}</title>}
            {description && <meta name='description' content={formattedDescription} />}
            {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}

            {title && <meta property='og:title' content={title} />}
            {description && <meta property='og:description' content={formattedDescription} />}
            {imageUrl && <meta property='og:image' content={imageUrl} />}
            <meta property='og:type' content='website' />

            {title && <meta name='twitter:title' content={title} />}
            {description && <meta name='twitter:description' content={formattedDescription} />}
            {imageUrl && <meta name='twitter:image' content={imageUrl} />}
            <meta name='twitter:card' content='summary_large_image' />

            {productSchema && (
                <script
                    data-load-point='headEnd'
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(productSchema)
                    }}
                />
            )}
        </HtmlHeadInclude>
    );
};
